import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  BookOpen, Gamepad2, Clock, Loader2, ShieldCheck, Sparkles, ArrowRight,
  ArrowLeft, Building2, CheckSquare, Printer,
} from "lucide-react";
import SEO from "@/components/SEO";
import useOnlineStatus from "@/hooks/useOnlineStatus";

/**
 * Audience-locked Academy hub.
 *   audience="renter"   → shown from the public landing page
 *   audience="business" → shown from the /businesses landing page
 *
 * Content with `audience === "both"` is intentionally NOT shown here -
 * the admin CMS now forces a binary choice so guides/quizzes can't bleed
 * across audiences.
 */
export default function Academy({ audience = "renter" }) {
  const isBiz = audience === "business";
  const [guides, setGuides]   = useState(null);
  const [quizzes, setQuizzes] = useState(null);
  const [checklists, setChecklists] = useState(null);
  const [err, setErr]         = useState(null);
  const online = useOnlineStatus();

  useEffect(() => {
    Promise.all([
      api.get("/academy/guides"),
      api.get("/academy/quizzes"),
      api.get("/academy/checklists"),
    ])
      .then(([g, q, c]) => { setGuides(g.data.items); setQuizzes(q.data.items); setChecklists(c.data.items); })
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  const filter = (items) => (items || []).filter((x) => x.audience === audience);

  const palette = isBiz
    ? { eyebrow: "Rental Business Guides & Games", badge: "For operators",
        heroTitle: "Rental Business Guides & Games",
        heroSub: "Operator-grade guides and decision quizzes: dispute prevention, fleet discipline, payment safety, and the small protocols that compound into 5-star reviews.",
        homeLabel: "Back to Businesses", homeTo: "/businesses",
        otherLabel: "Renter Guides & Games", otherTo: "/academy" }
    : { eyebrow: "Renter Guides & Games", badge: "For renters",
        heroTitle: "Renter Guides & Games",
        heroSub: "Practical guides and decision-style quizzes for Filipino renters and visitors. Free. No login required. Updated by the ConfirmPH team.",
        homeLabel: "Back to Home", homeTo: "/",
        otherLabel: "Rental Business Guides & Games", otherTo: "/businesses/academy" };

  return (
    <div data-testid={isBiz ? "business-academy-page" : "academy-page"} className="bg-[#FAFAFA] min-h-[80vh]">
      <SEO
        title={isBiz ? "Rental Business Guides & Games: ConfirmPH" : "Renter Guides & Games: ConfirmPH"}
        description={isBiz
          ? "Operator-grade guides and decision quizzes for Philippine rental businesses: dispute prevention, fleet discipline, payment safety, and customer follow-up."
          : "Free guides and decision-style quizzes for Filipino renters and visitors: practical safety, payment, and pickup advice updated by the ConfirmPH team."
        }
        canonical={isBiz ? "/businesses/academy" : "/academy"}
      />
      {/* HERO */}
      <section className="bg-[#0F1F18] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <Link to={palette.homeTo} data-testid="academy-home-back"
                 className="inline-flex items-center gap-1 text-emerald-300 text-xs hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" /> {palette.homeLabel}
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs uppercase tracking-[0.18em] font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" /> {palette.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-4 leading-tight max-w-3xl">
            {palette.heroTitle}
          </h1>
          <p className="text-emerald-50/80 text-base sm:text-lg mt-4 max-w-2xl">
            {palette.heroSub}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-14">
        {err && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 p-4 text-sm" data-testid="academy-error">
            {!online
              ? "You're offline: Guides & Games needs an internet connection to load."
              : err}
          </div>
        )}

        {/* GUIDES */}
        <section data-testid="academy-guides-section">
          <SectionHead icon={BookOpen} eyebrow="Guides & Articles"
                       title="Read the playbook"
                       sub={isBiz
                         ? "Field-tested protocols for the operator side of the rental."
                         : "Field-tested checklists and red flags for the renter side of the rental."} />
          {!guides ? <Loading />
           : filter(guides).length === 0
             ? <EmptyState message={isBiz ? "No business guides published yet." : "No renter guides published yet."} />
             : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                 {filter(guides).map((g) => <GuideCard key={g.id} g={g} isBiz={isBiz} />)}
               </div>}
        </section>

        {/* CHECKLISTS: only when there's at least one for this audience */}
        {checklists && filter(checklists).length > 0 && (
          <section data-testid="academy-checklists-section">
            <SectionHead
              icon={CheckSquare}
              eyebrow="Printable Checklists"
              title="Use the checklist"
              sub="Practical before/during/after checkboxes you can work through on your phone, print, or save as PDF." />
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
              {filter(checklists).map((c) => <ChecklistCard key={c.id} c={c} isBiz={isBiz} />)}
            </div>
          </section>
        )}

        {/* QUIZZES */}
        <section data-testid="academy-quizzes-section">
          <SectionHead icon={Gamepad2} eyebrow="Games & Quizzes"
                       title="Test your decisions"
                       sub="Real rental scenarios: pick the safest move. 10 questions, immediate feedback, and a quiz score at the end." />
          {!quizzes ? <Loading />
           : filter(quizzes).length === 0
             ? <EmptyState message={isBiz ? "No business quizzes published yet." : "No renter quizzes published yet."} />
             : <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                 {filter(quizzes).map((q) => <QuizCard key={q.id} q={q} isBiz={isBiz} />)}
               </div>}
        </section>

        {/* MINI GAMES */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
                 data-testid="academy-games-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center">
                <Gamepad2 className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="font-display text-base font-semibold">Learn by playing</div>
                <div className="text-sm text-slate-600">
                  Spot the Issue, Quick Quiz, and a word finder: quick, no-login games covering pickup/return checks and booking safety.
                </div>
              </div>
            </div>
            <Link to="/academy/games" data-testid="academy-games-link"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027] whitespace-nowrap">
              Play now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* CROSS-LINK to the other Academy */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
                 data-testid="academy-crosslink">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <div className="font-display text-base font-semibold">
                  {isBiz ? "Looking for the renter side?" : "Run a rental business?"}
                </div>
                <div className="text-sm text-slate-600">
                  {isBiz
                    ? "We also publish the renter playbook: useful if you onboard staff who'll handle customer-facing conversations."
                    : "Different guides and quizzes are written for car-rental operators (dispute prevention, fleet discipline, payment safety)."}
                </div>
              </div>
            </div>
            <Link to={palette.otherTo} data-testid="academy-crosslink-link"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-800 text-sm font-medium hover:bg-slate-50 whitespace-nowrap">
              {palette.otherLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* SIGN-IN CTA */}
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 sm:p-7"
                 data-testid="academy-signin-strip">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold inline-flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Save your progress
              </div>
              <div className="font-display text-xl font-semibold text-slate-900 mt-1">
                {isBiz ? "Want to track your team's training quiz scores?" : "Want to keep your quiz scores and learning badges?"}
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {isBiz
                  ? "Create a free business account, add your team, and run the operator quizzes as part of staff onboarding."
                  : "Create a free ConfirmPH account to save quiz results, get booking-linked review credit, and track your quiz scores over time. A quiz score is about what you know; it is not a ConfirmPH safety or readiness status."}
              </div>
            </div>
            <Link to={isBiz ? "/register?as=business" : "/register"}
                  data-testid="academy-cta-signup"
                  className="inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027] whitespace-nowrap">
              {isBiz ? "Create business account" : "Create free account"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionHead({ icon: Icon, eyebrow, title, sub }) {
  return (
    <div className="mb-5">
      <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {eyebrow}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-1">{title}</h2>
      <p className="text-slate-600 text-sm mt-1 max-w-2xl">{sub}</p>
    </div>
  );
}

function GuideCard({ g, isBiz }) {
  return (
    <Link to={`/academy/guide/${g.slug}`}
          data-testid={`academy-guide-card-${g.slug}`}
          className="group rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-emerald-300 transition">
      <div className="flex items-center justify-between text-xs">
        <span className={`px-2 py-0.5 rounded-full font-medium ${isBiz ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
          {isBiz ? "For operators" : "For renters"}
        </span>
        {g.category && <span className="text-slate-500">{g.category}</span>}
      </div>
      <div className="font-display text-lg font-semibold mt-3 group-hover:text-emerald-800">{g.title}</div>
      {g.subtitle && <div className="text-sm text-slate-600 mt-1.5 line-clamp-3">{g.subtitle}</div>}
      <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {g.reading_time_minutes || 5} min read</span>
        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium group-hover:underline">
          Read guide <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

function QuizCard({ q, isBiz }) {
  return (
    <Link to={`/academy/quiz/${q.slug}`}
          data-testid={`academy-quiz-card-${q.slug}`}
          className="group rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-emerald-300 transition">
      <div className="flex items-center justify-between text-xs">
        <span className={`px-2 py-0.5 rounded-full font-medium ${isBiz ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
          {isBiz ? "For operators" : "For renters"}
        </span>
        <span className="text-slate-500">{q.active_questions || 0} questions</span>
      </div>
      <div className="font-display text-lg font-semibold mt-3 group-hover:text-emerald-800">{q.title}</div>
      {q.description && <div className="text-sm text-slate-600 mt-1.5 line-clamp-3">{q.description}</div>}
      <div className="mt-4 flex items-center gap-3 text-xs">
        <span className="text-slate-500">{q.questions_per_session || 10} per session · randomised</span>
        <span className="ml-auto text-emerald-700 font-medium group-hover:underline inline-flex items-center gap-1">
          Start quiz <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

function ChecklistCard({ c, isBiz }) {
  return (
    <div data-testid={`academy-checklist-card-${c.slug}`}
         className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-emerald-300 transition">
      <div className="flex items-center justify-between text-xs">
        <span className={`px-2 py-0.5 rounded-full font-medium ${isBiz ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
          {isBiz ? "For operators" : "For renters"}
        </span>
        <span className="text-slate-500 inline-flex items-center gap-1">
          <Printer className="w-3.5 h-3.5" /> Printable
        </span>
      </div>
      <div className="font-display text-lg font-semibold mt-3 text-slate-900">{c.title}</div>
      {c.description && <div className="text-sm text-slate-600 mt-1.5 line-clamp-3">{c.description}</div>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to={`/academy/checklists/${c.slug}`}
              data-testid={`academy-checklist-view-${c.slug}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-xs font-medium hover:bg-[#143027]">
          View <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <Link to={`/academy/checklists/${c.slug}?print=1`}
              data-testid={`academy-checklist-print-${c.slug}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-800 text-xs font-medium hover:bg-slate-50">
          <Printer className="w-3.5 h-3.5" /> Print
        </Link>
        <Link to={`/academy/checklists/${c.slug}?print=1`}
              data-testid={`academy-checklist-download-${c.slug}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-800 text-xs font-medium hover:bg-slate-50">
          <BookOpen className="w-3.5 h-3.5" /> Download (PDF)
        </Link>
      </div>
    </div>
  );
}

function Loading()       { return <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>; }
function EmptyState({ message }) { return <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">{message}</div>; }
